<template>
  <div>
    <b-row>
      <b-col :cols="leftLarge ? 8 : 4">
        <b-row>
          <b-col>
            <h5>
              {{ trans('list_type_class_participants') }}
            </h5>
          </b-col>
          <b-col class="col-auto text-right">
            <b-button variant="link" class="p-0" @click="leftLarge = !leftLarge">
              <i :class="'fa fa-fw fa-angle-double-' + (leftLarge ? 'left' : 'right')"></i>
            </b-button>
          </b-col>
        </b-row>

        <b-card footer-tag="footer" class="mb-4" no-body>
          <b-tabs card>
            <b-tab :title="'1. '+trans('import')">
              <b-form-group v-for="(rule, index) in rules" :key="index">
                <class-participants-rule
                  :organization="organization"
                  :recipient_list="recipient_list"
                  v-model="rules[index]"
                  :show-delete="index > 0"
                  :all-classes="all_classes"
                  @all-classes="all_classes = $event"
                  :all-tags="all_tags"
                  @all-tags="all_tags = $event"
                  @delete="onDeleteRule(index)"
                ></class-participants-rule>
              </b-form-group>

              <div>
                <b-button variant="outline-primary" size="sm" :title="trans('add_participants')" @click="onAddRule('add')">
                  <i class="fa fa-fw fa-plus"></i>
                  <i class="fa fa-fw fa-users"></i>
                </b-button>
                <b-button variant="outline-primary" size="sm" :title="trans('remove_participants')" @click="onAddRule('remove')" class="ml-4">
                  <i class="fa fa-fw fa-minus"></i>
                  <i class="fa fa-fw fa-users"></i>
                </b-button>
              </div>

              <div class="mt-3">
                <b-form-group>
                  <label><strong>{{ trans('combine_options') }}</strong></label>
                  <div v-if="!combine && !allow_multiple">
                    {{ trans('import_single_class') }}
                    <small class="text-muted" :title="trans('import_single_class_about')" v-b-tooltip.hover>
                      <i class="fa fa-fw fa-info-circle"></i>
                    </small>
                  </div>
                  <div v-if="combine && !allow_multiple">
                    {{ trans('import_combine_duplicates') }}
                    <small class="text-muted" :title="trans('import_combine_duplicates_about')" v-b-tooltip.hover>
                      <i class="fa fa-fw fa-info-circle"></i>
                    </small>
                  </div>
                  <div v-if="!combine && allow_multiple">
                    {{ trans('import_allow_multiple_class') }}
                    <small class="text-muted" :title="trans('import_allow_multiple_class_about')" v-b-tooltip.hover>
                      <i class="fa fa-fw fa-info-circle"></i>
                    </small>
                  </div>
                  <b-button variant="outline-primary" class="py-0" v-b-modal.combine-options-modal>
                    {{ trans('modify') }}
                  </b-button>
                  <combine-options-modal
                    what="class"
                    :combine="combine"
                    @combine="combine = $event"
                    :allow_multiple="allow_multiple"
                    @allow_multiple="allow_multiple = $event"
                  ></combine-options-modal>
                </b-form-group>
                <b-form-group>
                  <label>
                    {{ trans('only_active') }}
                    <small class="text-muted" :title="trans('only_active_about')" v-b-tooltip.hover>
                      <i class="fa fa-fw fa-info-circle"></i>
                    </small>
                  </label>
                  <b-form-checkbox switch v-model="filters.only_active">
                    {{ trans(filters.only_active ? 'yes' : 'no') }}
                  </b-form-checkbox>
                </b-form-group>
              </div>
            </b-tab>
            <b-tab :title="filterTabTitle">
              <import-filters
                :type="recipient_list.type"
                v-model="filters"
                :can-filter="canFilter"
              />
            </b-tab>
          </b-tabs>

          <template v-slot:footer>
            <p>
              {{ participantCount }} {{ trans('recipient' + (participantCount > 1 || participantCount == 0 && lang == 'en' ? 's': '')) }}
            </p>
            <p v-if="busy">
              <i class="fa fa-fw fa-circle-notch fa-spin"></i>
              {{ trans('please_wait') }}
            </p>
            <p v-if="error" class="text-danger">
              {{ error }}
            </p>
            <p>
              <b-button variant="primary" :disabled="!canSave" @click="onSave">
                {{ trans('save') }}
              </b-button>
            </p>
          </template>
        </b-card>
      </b-col>
      <b-col :cols="leftLarge ? 4 : 8">
        <b-row>
          <b-col>
            <h5>
              {{ trans('recipients') }}
              <small>
                ({{ trans((filters.recipient_filter)+'s').toLowerCase() }})
                <i class="fa fa-fw fa-info-circle text-muted" id="recipients_about"></i>
                <b-tooltip target="recipients_about" triggers="hover">
                  {{ trans('recipients_about') }}
                </b-tooltip>
              </small>
            </h5>
          </b-col>
          <b-col class="col-auto">
            <b-button
              variant="link"
              size="sm"
              class="text-success"
              :href="excelUrl"
              target="_blank"
              :disabled="busy"
            >
              <i class="fa fa-fw fa-file-excel"></i>
              {{ trans('download') }}
            </b-button>
          </b-col>
        </b-row>
        <b-table
          id="recipients"
          ref="table"
          class="bg-white"
          bordered
          small
          hover
          responsive
          show-empty
          :per-page="perPage"
          :current-page="currentPage"
          :fields="fields"
          :items="recipients"
        >
          <template v-slot:empty>
            <h4 v-if="busy" align="center">
              <i class="fa fa-circle-notch fa-spin"></i>
              {{ trans('loading') }}
            </h4>
            <p v-if="!busy" align="center">{{ trans('no_participants_found') }}</p>
          </template>
          <template v-slot:cell(account_owner)="data">
            <small>{{ data.value }}</small>
          </template>
          <template v-slot:cell(participant)="data">
            <small>{{ data.value }}</small>
          </template>
          <template v-slot:cell(class_name)="data">
            <small>{{ data.value }}</small>
          </template>
        </b-table>
        <b-pagination
          :total-rows="participantCount"
          :per-page="perPage"
          v-model="currentPage"
          aria-controls="recipients"
          align="center"
        ></b-pagination>
      </b-col>
    </b-row>
    <br/>
  </div>
</template>

<script>
  import ClassParticipantsRule from './ClassParticipantsRule'
  import ImportFilters from './ImportFiltersComponent'
  import CombineOptionsModal from './CombineOptionsModal'

  const blankRule = {
    name: null,
    type: 'add',
    what: 'classes',
    am_class_ids: null,
    tags: null,
    participant_count: null,
    class_count: null,
    participants: [],
  }

  export default {
    props: {
      organization: Object,
      recipient_list: Object,
    },
    components: {
      ClassParticipantsRule,
      ImportFilters,
      CombineOptionsModal,
    },
    data() {
      let rules = this.recipient_list.meta ?
        this.recipient_list.meta.rules :
        [_.clone(blankRule)]
      let filters = this.recipient_list.meta ?
        this.recipient_list.meta.filters :
        null
      return {
        trans: trans,
        lang: lang,
        busy: false,
        error: null,
        leftLarge: false,
        items: null,
        all_classes: null,
        all_tags: null,
        rules: rules,
        filters: {
          age_filter: filters ? filters.age_filter : 'no',
          age_min: filters ? filters.age_min : 0,
          age_max: filters ? filters.age_max : 99,
          birthday_filter: filters ? filters.birthday_filter : 'no',
          birthday_month: filters ? filters.birthday_month : moment().startOf('month').format('YYYY-MM-DD'),
          gender_filter: filters ? filters.gender_filter : 'no',
          recipient_filter: filters ? filters.recipient_filter : 'participant',
          only_active: filters ? filters.only_active : false,
        },
        combine: this.recipient_list.amilia_combine,
        allow_multiple: this.recipient_list.amilia_allow_multiple,
        perPage: 10,
        currentPage: 1,
      }
    },
    computed: {
      canFilter() {
        return !this.busy && this.rules.length > 0
      },
      filterTabTitle() {
        let str = '2. '+trans('filter')
        let count = 0
        if (this.filters.age_filter != 'no') count += 1
        if (this.filters.birthday_filter != 'no') count += 1
        if (this.filters.gender_filter != 'no') count += 1
        if (this.filters.recipient_filter != 'participant') count += 1
        if (count > 0) str += ' (' + count + ')'
        return str
      },
      canSave() {
        return this.canFilter && !this.busy
      },
      recipients() {
        let ids = []
        let emails = []
        let pairs = []
        return _.reduce(this.rules, (recipients, rule) => {
          return _.reduce(rule.participants || [], (recipients, p) => {

            if (this.filters.only_active) {
              if (_.includes(this.notActiveRecipientIds, p.id)) return recipients
            }

            if (!p.email) return recipients
            let r = this.mapParticipantToRecipient(p)

            let index = ids.indexOf(r.id)
            if (rule.type == 'add' && index === -1) {
              if (this.allow_multiple) {
                if (this.passFilter(r)) {
                  recipients.push(r)
                  ids.push(r.id)
                }
              }
              else if (this.combine) {
                if (emails.indexOf(r.email) === -1) {
                  emails.push(r.email)
                  if (this.passFilter(r)) {
                    recipients.push(r)
                    ids.push(r.id)
                  }
                }
              } else {
                // One class per participant
                let key = r.email + '.' + (r.name || '')
                if (pairs.indexOf(key) === -1) {
                  pairs.push(key)
                  if (this.passFilter(r)) {
                    recipients.push(r)
                    ids.push(r.id)
                  }
                }
              }
            }
            else if (rule.type == 'remove') {
              if (index >= 0) ids.splice(index, 1)
              index = emails.indexOf(r.email)
              if (index >= 0) emails.splice(index, 1)
              index = _.find(recipients, {id: r.id})
              if (index >= 0) recipients.splice(index, 1)
              recipients = _.reduce(recipients, (recipients, o) => {
                if (o.email != r.email) recipients.push(o)
                let key = o.email + '.' + (o.name || '')
                let index = pairs.indexOf(key)
                if (index >= 0) pairs.splice(index, 1)
                return recipients
              }, [])
            }
            return recipients
          }, recipients)
        }, [])
      },
      fields() {
        return _.compact([{
          key: 'name',
          label: trans(this.filters.recipient_filter),
          formatter: (value, key, item) => {
            return this.filters.recipient_filter == 'account_owner' ?
              this.tableAccountOwner(item) :
              this.tableParticipant(item)
          },
          sortable: true,
        }, {
          key: 'email',
          label: trans('email'),
          sortable: true,
        }, {
          key: 'mobile',
          label: trans('mobile'),
          sortable: true,
        }, this.filters.recipient_filter == 'account_owner' ? {
          key: 'participant',
          label: trans('participant'),
          formatter: (value, key, item) => {
            return this.tableParticipant(item)
          },
        } : null, this.filters.recipient_filter == 'participant' ? {
          key: 'account_owner',
          label: trans('account_owner'),
          formatter: (value, key, item) => {
            return this.tableAccountOwner(item)
          },
        } : null, {
          key: 'class_name',
          label: trans('class'),
          sortable: true,
        }])
      },
      participantCount() {
        return this.recipients.length
      },
      validEmailCount() {
        return _.reduce(this.recipients, (count, r) => {
          if (_.validateEmail(r.email) === true) count += 1
          return count
        }, 0)
      },
      validMobileCount() {
        return _.reduce(this.recipients, (count, r) => {
          let number = (r.mobile||'').replace(/\D/g,'').replace(/^1/, '')
          if (number.length == 10) count += 1
          return count
        }, 0)
      },
      serialized() {
        return {
          meta: {
            rules: _.map(this.rules, rule => {
              return _.omit(rule, 'participants')
            }),
            filters: this.filters,
          },
          amilia_combine: this.combine,
          amilia_allow_multiple: this.allow_multiple,
          participant_count: this.participantCount,
          valid_email_count: this.validEmailCount,
          valid_mobile_count: this.validMobileCount,
        }
      },
      notActiveRecipientIds() {
        let not_active_ids = []
        let start_of_day = new Date()
        start_of_day.setHours(0, 0, 0, 0)
        _.each(this.rules, rule => {
          _.each(rule.participants, participant => {
            if (participant.dates.length == 0) not_active_ids.push(participant.id)
            else {
              if(!_.some(participant.dates, date => {
                return new Date(date).getTime() > start_of_day;
              })) not_active_ids.push(participant.id)
            }
          });
        })
        return not_active_ids
      },
      excelUrl() {
        return window.location.href + '/excel'
      },
    },
    methods: {
      tableParticipant(r) {
        let gender = null
        switch (gender) {
          case 'Male':
            gender = trans('male')
            break
          case 'Female':
            gender = trans('female')
            break
          case 'NotSpecified':
            gender = trans('not_specified')
            break
        }
        let date_of_birth = null
        if (r.date_of_birth) {
          date_of_birth = moment(r.date_of_birth).format('D MMM') + (r.age ? ` (${r.age})` : '')
        }
        return _.compact([
          r.participant_name,
          gender,
          date_of_birth,
          r.participant_email != r.email ? r.participant_email : null,
          r.participant_mobile != r.mobile ? r.participant_mobile : null,
        ]).join(', ')
      },
      tableAccountOwner(r) {
        return _.compact([
          r.account_owner_name,
          r.account_owner_email != r.email ? r.account_owner_email : null,
          r.account_owner_mobile != r.mobile ? r.account_owner_mobile : null,
        ]).join(', ')
      },
      mapParticipantToRecipient(p) {
        let name = p.name
        let email = p.email
        let mobile = p.mobile
        if (this.filters.recipient_filter == 'account_owner') {
          name = p.account_owner.name
          email = p.account_owner.email || email
          mobile = p.account_owner.mobile || mobile
        }
        return {
          id: p.id,
          name: name,
          email: email,
          mobile: mobile,
          age: p.age,
          gender: p.gender,
          date_of_birth: p.date_of_birth,
          is_child: !!p.child,
          participant_name: p.name,
          participant_email: p.email,
          participant_mobile: p.mobile,
          account_owner_name: p.account_owner.name,
          account_owner_email: p.account_owner.email,
          account_owner_mobile: p.account_owner.mobile,
          class_name: p.class_name,
        }
      },
      passFilter(r) {
        let agePass = this.filters.age_filter == 'no' ||
          this.filters.age_filter == 'children' && r.is_child ||
          this.filters.age_filter == 'adults' && !r.is_child ||
          this.filters.age_filter == 'range' && r.age != null && r.age >= this.filters.age_min && r.age <= this.filters.age_max

        let bdayPass = this.filters.birthday_filter == 'no' ||
          r.date_of_birth != null && moment(this.filters.birthday_month).format('M') == moment(r.date_of_birth).format('M')

        let genderPass = this.filters.gender_filter == 'no' ||
          this.filters.gender_filter.charAt(0) == (r.gender||' ').charAt(0).toLowerCase()

        return agePass && bdayPass && genderPass
      },
      onSave() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/list/${this.recipient_list.id}/class_participants`
        axios.patch(url, this.serialized)
        .then(response => {
          this.busy = false
          this.$emit('list', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onAddRule(type) {
        let rule = _.clone(blankRule)
        rule.type = type
        this.rules.push(rule)
      },
      onDeleteRule(index) {
        this.rules.splice(index, 1)
      },
    },
    mounted() {
      window.classParticipantsList = this
    },
  }
</script>